import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, User, Loader2, Lock as LockIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { IsometricStack, FloorPlanFragment } from "@/components/landing/Blueprint";
import { isPublicRegistrationEnabled } from "@/lib/app-settings";

const ease = [0.22, 1, 0.36, 1] as [number, number, number, number];

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  // Starts closed so the sign-up form is never briefly offered while the
  // setting is still loading, then only opens if the setting says so.
  const [registrationOpen, setRegistrationOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    isPublicRegistrationEnabled().then((open) => {
      if (!cancelled) setRegistrationOpen(open);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // If registration closes while someone is sitting on the sign-up form, put
  // them back on sign-in rather than leaving a form that cannot submit.
  useEffect(() => {
    if (!registrationOpen) setIsLogin(true);
  }, [registrationOpen]);

  useEffect(() => {
    const routeForSession = async (userId: string) => {
      try {
        const { data } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .eq("role", "admin")
          .maybeSingle();
        navigate(data ? "/admin/dashboard" : "/deals-v2/dashboard");
      } catch {
        navigate("/deals-v2/dashboard");
      }
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) void routeForSession(session.user.id);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) void routeForSession(session.user.id);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        toast({ title: "Welcome back" });
      } else {
        if (!registrationOpen) {
          navigate("/waitlist");
          return;
        }
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { full_name: fullName },
            emailRedirectTo: `${window.location.origin}/auth`,
          },
        });
        if (error) throw error;
        if (data.session) {
          toast({ title: "Welcome to Brickshare.ai" });
        } else {
          toast({
            title: "Check your email",
            description: "We've sent you a confirmation link to verify your account.",
          });
        }
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[hsl(var(--bone))] text-[hsl(var(--ink))]">
      {/* Atmospheric backdrop */}
      <div className="brand-aurora" aria-hidden="true" />
      <div className="absolute inset-0 brand-grid-bg opacity-60 pointer-events-none [mask-image:radial-gradient(ellipse_60%_60%_at_50%_40%,#000_30%,transparent_80%)]" aria-hidden="true" />

      {/* Top brand bar */}
      <header className="relative z-10 px-6 lg:px-10 py-6 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 group">
          <div className="w-8 h-8 rounded bg-[hsl(var(--brand-forest))] flex items-center justify-center">
            <span className="font-serif italic text-[hsl(var(--bone))] text-sm font-medium">B</span>
          </div>
          <span className="font-sans text-[15px] font-semibold tracking-tight">
            Brickshare<span className="text-[hsl(var(--brand-ochre))]">.ai</span>
          </span>
        </button>
        <button
          onClick={() => navigate("/")}
          className="link-underline font-sans text-[13px] font-medium text-[hsl(var(--stone-600))] hover:text-[hsl(var(--ink))] transition-colors"
        >
          Back to site
        </button>
      </header>

      <div className="relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-16 max-w-[1200px] mx-auto px-6 lg:px-10 pt-6 lg:pt-12 pb-20">
        {/* Left — editorial copy */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease }}
          className="lg:col-span-6 hidden lg:flex flex-col justify-center relative"
        >
          <div className="inline-flex w-fit items-center gap-2 px-3.5 py-1.5 rounded-full border border-[hsl(var(--stone-200))] bg-[hsl(var(--paper))]/80 backdrop-blur-sm shadow-[0_1px_2px_rgba(11,15,13,0.04)] mb-7">
            <LockIcon className="w-3.5 h-3.5 text-[hsl(var(--brand-ochre))]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-[hsl(var(--stone-600))]">
              Private Beta — Access by Approval
            </span>
          </div>

          <h1 className="font-serif text-[clamp(2.75rem,5.5vw,4.25rem)] leading-[1.0] tracking-[-0.03em] font-normal">
            <span className="block">{isLogin ? "Welcome" : "Create your"}</span>
            <span className="block">
              {isLogin ? (
                <>
                  back to <span className="italic font-medium text-[hsl(var(--brand-forest))]">Brickshare</span>.
                </>
              ) : (
                <>
                  <span className="italic font-medium text-[hsl(var(--brand-forest))]">workspace</span>.
                </>
              )}
            </span>
          </h1>

          <div className="brand-rule mt-6 mb-7" />

          <p className="font-sans text-[17px] leading-[1.55] text-[hsl(var(--stone-600))] max-w-md">
            {isLogin
              ? "Pick up where you left off. Your deals, scenarios, and AI conversations are waiting."
              : "Analyze deals, detect hidden risk, and uncover negotiation insights — in minutes, not days."}
          </p>

          <div className="mt-12 relative h-[180px]">
            <IsometricStack className="absolute -left-6 top-0 w-[280px] text-[hsl(var(--brand-forest-deep))] opacity-[0.10]" />
            <FloorPlanFragment className="absolute right-0 bottom-0 w-[220px] text-[hsl(var(--brand-forest-deep))] opacity-[0.08]" />
          </div>
        </motion.div>

        {/* Right — form card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.1 }}
          className="lg:col-span-6 flex items-center justify-center"
        >
          <div className="w-full max-w-md brand-card p-8 lg:p-10">
            <div className="mb-7">
              <p className="brand-eyebrow mb-2">{isLogin ? "Sign In" : "Create Account"}</p>
              <h2 className="font-serif text-3xl tracking-[-0.02em] text-[hsl(var(--ink))]">
                {isLogin ? "Sign in to continue" : "Join the platform"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <div className="space-y-1.5">
                  <Label htmlFor="fullName" className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">
                    Full Name
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--stone-400))]" />
                    <Input
                      id="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Your name"
                      className="pl-10 h-11 bg-[hsl(var(--bone))] border-[hsl(var(--stone-200))] focus-visible:ring-[hsl(var(--brand-forest))] focus-visible:border-[hsl(var(--brand-forest))]"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--stone-400))]" />
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10 h-11 bg-[hsl(var(--bone))] border-[hsl(var(--stone-200))] focus-visible:ring-[hsl(var(--brand-forest))] focus-visible:border-[hsl(var(--brand-forest))]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-[0.1em] text-[hsl(var(--stone-600))]">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--stone-400))]" />
                  <Input
                    id="password"
                    type="password"
                    required
                    minLength={6}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 h-11 bg-[hsl(var(--bone))] border-[hsl(var(--stone-200))] focus-visible:ring-[hsl(var(--brand-forest))] focus-visible:border-[hsl(var(--brand-forest))]"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-forest font-sans text-[14px] font-medium h-12 px-6 rounded-[10px] w-full inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>{isLogin ? "Sign In" : "Create Account"}</span>
                    <ArrowRight className="btn-arrow w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-7 pt-6 border-t border-[hsl(var(--stone-200))]/60 text-center">
              <button
                onClick={() => {
                  // With registration closed, "Request access" means exactly
                  // that: the waitlist, not a form that creates an account.
                  if (isLogin && !registrationOpen) navigate("/waitlist");
                  else setIsLogin(!isLogin);
                }}
                className="link-underline font-mono text-[12px] text-[hsl(var(--stone-600))] hover:text-[hsl(var(--ink))] transition-colors"
              >
                {isLogin ? "Don't have an account? Request access" : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Auth;
